/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */

import { useLanguage } from '@infominds/react-native-components'
import { useAuthentication } from '@infominds/react-native-license'
import { useNavigation } from '@react-navigation/native'
import Moment from 'moment'
import React, { useEffect, useMemo, useState } from 'react'
import { FlatList, StyleSheet } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import api from '../apis/Api'
import Colors from '../constants/Colors'
import useDisableBottomNavigation from '../hooks/useDisableBottomNavigation'
import { Bill, HomeScreenNavigationProp } from '../types'
import { PriceUtils } from '../utils/priceUtils'
import StateBadge from './StateBadge'
import { Text, TouchableOpacity, View } from './Themed'

interface Props {
  bill: Bill
}

function CommentView({ bill }: Props) {
  const { i18n, language } = useLanguage()
  const { bottom } = useSafeAreaInsets()
  const navigation = useNavigation<HomeScreenNavigationProp>()
  useDisableBottomNavigation()
  const { url, token, company } = useAuthentication()

  const [history, setHistory] = useState([])
  const [refreshing, setRefreshing] = useState(true)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [detail, setDetail] = useState<any>()
  const basePrice = useMemo(() => `${i18n.t('BASEPRICE')}: ${PriceUtils.formatPrice(bill.vatBasisAmount ?? 0, language, bill.currency)}`, [bill])
  const vatPrice = useMemo(() => `${i18n.t('VAT')}: ${PriceUtils.formatPrice(bill.vatAmount ?? 0, language, bill.currency)}`, [bill])
  const totalPrice = useMemo(() => `${i18n.t('TOTAL')}: ${PriceUtils.formatPrice(bill.billAmount ?? 0, language, bill.currency)}`, [bill])

  useEffect(() => {
    load()
    api
      .getBillDetail(token, url, company, bill.billID, bill.supplierID, bill.billDate)
      .then(res => {
        setDetail(res)
      })
      .catch(console.error)
  }, [bill, token, url, company])

  function format(amount: number, currency: string, id: number, description: string): string {
    let result = amount > 0 ? PriceUtils.formatPrice(amount, language, currency) : ''

    if (id > 0) {
      if (result !== '') result += ' - ' + id.toString()
      else result += id
    }

    if (description !== '') {
      if (result !== '') result += ' - ' + description
      else result += description
    }

    return result
  }

  const load = () => {
    api
      .getHistory(token, url, company, bill.billID, bill.supplierID, bill.billDate)
      .then(result => {
        setHistory(result as never)
      })
      .catch(error => {
        console.error(error)
      })
      .finally(() => setRefreshing(false))
  }

  const renderItem = ({ item }: any) => {
    return (
      <View>
        <View style={styles.item} lightColor={Colors.light.listItemBackground} darkColor={Colors.dark.listItemBackground}>
          <View style={styles.itemLeft} lightColor={Colors.light.listItemBackground} darkColor={Colors.dark.listItemBackground}>
            <Text style={styles.itemTitle}>
              {item.firstName} {item.lastName}
            </Text>

            {item.updated === '0001-01-01T00:00:00' ? (
              <Text lightColor={Colors.light.listItemDetail} darkColor={Colors.dark.listItemDetail}>
                {Moment().format('DD/MM/yyyy')}
              </Text>
            ) : (
              <Text lightColor={Colors.light.listItemDetail} darkColor={Colors.dark.listItemDetail}>
                {Moment(item.updated).format('DD/MM/yyyy')}
              </Text>
            )}

            <Text lightColor={Colors.light.listItemDetail} darkColor={Colors.dark.listItemDetail}>
              {item.note}
            </Text>
          </View>
          <View style={styles.itemRight} lightColor={Colors.light.listItemBackground} darkColor={Colors.dark.listItemBackground}>
            <StateBadge state={item.billState} isOthers={false} hasComment={item.msgCount > 0} />
          </View>
        </View>
      </View>
    )
  }

  return (
    // eslint-disable-next-line react-native/no-inline-styles
    <View style={{ height: '100%' }}>
      <View style={styles.container}>
        <View style={styles.left}>
          <Text style={styles.header}>{bill.supplierName}</Text>
          <View style={styles.detail}>
            <Text>
              {i18n.t('ID_BILLNR')} {bill.billID} {i18n.t('ID_FROM')} {Moment(bill.billDate).format('DD/MM/yyyy')}
            </Text>
            <Text>{basePrice}</Text>
            <Text>{vatPrice}</Text>
            <Text>{totalPrice}</Text>

            {bill.documentReleaseNote && (
              <View>
                <Text style={styles.textBold}>
                  {'\n'}
                  {i18n.t('NOTE_DOCUMENT')}
                </Text>
                <Text>{bill.documentReleaseNote}</Text>
              </View>
            )}

            <Text style={styles.textBold}>
              {'\n'}
              {i18n.t('DEADLINE')}:
            </Text>
            {/* @ts-ignore todo */}
            {bill.dueDate?.map((dd: unknown, index: string) => {
              return (
                <Text key={index}>
                  {/* @ts-ignore todo */}
                  {PriceUtils.formatPrice(dd.amount, language, bill.currency)} - {Moment(dd.date).format('DD/MM/yyyy')}
                </Text>
              )
            })}

            {detail ? (
              <View>
                {detail.accounting.length > 0 && (
                  <Text style={styles.textBold}>
                    {'\n'}
                    {i18n.t('ACCOUNT')}:
                  </Text>
                )}
                {detail.accounting.map((item: any, index: string) => {
                  return <Text key={index}>{format(item.amount, bill.currency ?? '', item.id, item.description)}</Text>
                })}

                {detail.cdC.length > 0 && (
                  <Text style={styles.textBold}>
                    {'\n'}
                    {i18n.t('CDC')}:
                  </Text>
                )}
                {detail.cdC.map((item: any, index: string) => {
                  return <Text key={index}>{format(item.amount, bill.currency ?? '', item.id, item.description)}</Text>
                })}

                {detail.objects.length > 0 && (
                  <Text style={styles.textBold}>
                    {'\n'}
                    {i18n.t('OBJECT')}:
                  </Text>
                )}
                {detail.objects.map((item: any, index: string) => {
                  return <Text key={index}>{format(item.amount, bill.currency ?? '', item.id, item.description)}</Text>
                })}
              </View>
            ) : (
              <></>
            )}
          </View>
        </View>
        {/* eslint-disable-next-line react-native/no-inline-styles */}
        <View style={{ flexDirection: 'row' }}>
          <TouchableOpacity
            lightColor={Colors.light.listItemBackground}
            darkColor={Colors.dark.listItemBackground}
            // eslint-disable-next-line react-native/no-inline-styles
            style={[styles.button, styles.textButton, { width: '50%' }]}
            onPress={() => navigation.navigate('PreviewScreen', { bill: bill })}>
            <Text>{i18n.t('PREVIEW')}</Text>
          </TouchableOpacity>
        </View>
      </View>

      <FlatList
        data={history}
        renderItem={renderItem}
        keyExtractor={(item, index) => index.toString()}
        onRefresh={() => {
          load()
        }}
        refreshing={refreshing}
        style={{ marginBottom: bottom }}
      />
    </View>
  )
}

const styles = StyleSheet.create({
  textBold: {
    fontWeight: 'bold',
  },
  container: {
    padding: 20,
  },
  left: {
    width: '70%',
  },
  header: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 10,
  },
  detail: {
    marginBottom: 20,
  },
  item: {
    margin: 2,
    padding: 10,
    fontSize: 18,
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  itemTitle: {
    fontSize: 16,
    fontWeight: 'bold',
  },
  itemLeft: {
    width: '70%',
  },
  itemRight: {
    padding: 5,
    alignSelf: 'flex-end',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    marginBottom: 10,
    alignItems: 'center',
    borderRadius: 5,
    padding: 5,
  },
  textButton: {
    padding: 10,
  },
})

export default CommentView
